import {
  ASSIGNEE_KIND_AGENT,
  ASSIGNEE_KIND_CHATBOT,
  ASSIGNEE_KIND_QUEUE,
  ASSIGNEE_KIND_RECEPTIONIST,
  ASSIGNEE_KIND_USER,
  buildAssigneeOption,
  buildAssigneeTransferOptions,
  getAgentId,
  isChatBotUser,
  resolveUserKind,
} from "./assigneeTransferField";

/** Converte `transfer` do lifecycle da skill em seleção do select unificado. */
export function lifecycleTransferToSelection(transfer = {}, { users = [], agents = [], labels = {} } = {}) {
  const dest = String(transfer?.dest || "none").trim().toLowerCase();
  const targetId = transfer?.target_id != null ? String(transfer.target_id).trim() : "";

  if (!dest || dest === "none") return [];

  if (dest === "fila_humano") {
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_QUEUE,
        id: "fila_humano",
        label: labels.queue || "Fila de atendimento humano",
      }),
    ].filter(Boolean);
  }

  if (dest === "receptionist") {
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_RECEPTIONIST,
        id: "receptionist",
        label: labels.receptionist || "Recepcionista (agente de IA)",
      }),
    ].filter(Boolean);
  }

  if (dest === "agente_ia" && targetId) {
    const agent = (agents || []).find((a) => String(getAgentId(a)) === targetId);
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_AGENT,
        id: targetId,
        label: agent?.name ?? agent?.nome ?? labels.agentFallback ?? `Agente #${targetId}`,
        raw: agent,
      }),
    ].filter(Boolean);
  }

  if (dest === "chatbot" && targetId) {
    const numId = Number(targetId);
    const user = (users || []).find((u) => Number(u.id) === numId);
    const name = user?.nome ?? user?.name ?? labels.chatbotFallback ?? `ChatBot #${targetId}`;
    return [
      buildAssigneeOption({
        kind: ASSIGNEE_KIND_CHATBOT,
        id: numId,
        label: name.endsWith("(ChatBot)") ? name : `${name} (ChatBot)`,
        raw: user,
      }),
    ].filter(Boolean);
  }

  if (dest === "humano" && targetId) {
    const ids = targetId.split(",").map((s) => s.trim()).filter(Boolean);
    return ids
      .map((id) => {
        const numId = Number(id);
        if (!Number.isFinite(numId) || numId <= 0) return null;
        const user = (users || []).find((u) => Number(u.id) === numId);
        const kind = resolveUserKind(user, numId);
        return buildAssigneeOption({
          kind,
          id: numId,
          label: user?.nome ?? user?.name ?? `Usuário #${numId}`,
          raw: user,
        });
      })
      .filter(Boolean);
  }

  return [];
}

/** Converte seleção unificada para `transfer` persistido na skill. */
export function selectionToLifecycleTransfer(selection = [], transferExtras = {}) {
  const list = Array.isArray(selection) ? selection.filter(Boolean) : [];
  const extras = transferExtras && typeof transferExtras === "object" ? transferExtras : {};
  const loggedUsersOnly =
    extras.logged_users_only === true ||
    extras.logged_users_only === 1 ||
    String(extras.somenteusuarioslogados || "")
      .trim()
      .toLowerCase() === "sim";
  const base = {
    dest: "none",
    target_id: "",
    tipo_id: extras.tipo_id != null ? String(extras.tipo_id) : "",
    tipo1_id: extras.tipo1_id != null ? String(extras.tipo1_id) : "",
    tipo2_id: extras.tipo2_id != null ? String(extras.tipo2_id) : "",
    tipo3_id: extras.tipo3_id != null ? String(extras.tipo3_id) : "",
    logged_users_only: false,
  };

  if (!list.length) return base;

  const first = list[0];
  if (first.kind === ASSIGNEE_KIND_QUEUE) {
    return { ...base, dest: "fila_humano", target_id: "" };
  }
  if (first.kind === ASSIGNEE_KIND_RECEPTIONIST) {
    return { ...base, dest: "receptionist", target_id: "" };
  }
  if (first.kind === ASSIGNEE_KIND_AGENT) {
    return { ...base, dest: "agente_ia", target_id: String(first.id) };
  }
  if (first.kind === ASSIGNEE_KIND_CHATBOT) {
    return { ...base, dest: "chatbot", target_id: String(first.id) };
  }

  const humanIds = list
    .filter((item) => item.kind === ASSIGNEE_KIND_USER || item.kind === ASSIGNEE_KIND_CHATBOT)
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!humanIds.length) return base;

  if (humanIds.length === 1) {
    const user = list[0]?.raw;
    const kind = user ? resolveUserKind(user, humanIds[0]) : list[0]?.kind;
    if (kind === ASSIGNEE_KIND_CHATBOT || list[0]?.kind === ASSIGNEE_KIND_CHATBOT) {
      return { ...base, dest: "chatbot", target_id: String(humanIds[0]) };
    }
    return {
      ...base,
      dest: "humano",
      target_id: String(humanIds[0]),
      logged_users_only: loggedUsersOnly,
    };
  }

  return {
    ...base,
    dest: "humano",
    target_id: humanIds.join(","),
    logged_users_only: loggedUsersOnly,
  };
}

/** Checkbox “somente logados” só faz sentido com destino humano. */
export function lifecycleTransferAllowsLoggedUsersOnly(transfer = {}) {
  return (
    String(transfer?.dest || "").toLowerCase() === "humano" &&
    String(transfer?.target_id || "").trim() !== ""
  );
}

export function buildSkillLifecyclePrefixOptions(labels = {}) {
  return [
    buildAssigneeOption({
      kind: ASSIGNEE_KIND_QUEUE,
      id: "fila_humano",
      label: labels.queue || "Fila de atendimento humano",
    }),
    buildAssigneeOption({
      kind: ASSIGNEE_KIND_RECEPTIONIST,
      id: "receptionist",
      label: labels.receptionist || "Recepcionista (agente de IA)",
    }),
  ].filter(Boolean).map((opt) => ({ ...opt, group: labels.shortcutsGroup || "Atalhos" }));
}

export function buildSkillLifecycleTransferOptions(ctx) {
  const prefix = buildSkillLifecyclePrefixOptions(ctx?.labels || {});
  const dynamic = buildAssigneeTransferOptions({
    users: ctx?.users,
    agents: ctx?.agents,
    excludeAgentIds: ctx?.excludeAgentIds,
  });
  return [...prefix, ...dynamic];
}
